import {
  Component,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import config from "../../../environment/environment-config";
import { QuantityUpdate } from "@apis/model/add-to-cart-event";

/**
 * @author lup1bg, kcr8wz
 * @date 2019-03
 * @licensor Robert Bosch GmbH, 2019
 * @version 1.0.0
 */

@Component({
  tag: "bcp-quantity-update",
  styleUrl: "bcp-quantity-update.scss",
})
export class BcpQuantityUpdate {
  /**
   * Initial value of quantity spinner.
   * If not set, '1' is used as a default value.
   */
  @Prop({ mutable: true, reflect: true }) initialValue = 1;

  /**
   * Minimum allowed value for quantity spinner.
   * If not set, '1' is used as a default value.
   */
  @Prop({ mutable: true }) minimumQuantity = 1;

  /**
   * Maximum allowed value for quantity spinner.
   * If not set, '99999' is used as a default value.
   */
  @Prop({ mutable: true }) maximumQuantity: number =
    config.cartConfig.defaultMaxQuantity;

  @Prop({ mutable: true, reflect: true }) quantity: number;

  /** Length of the number of digits for the
   *  default maximum quantity
   */
  @Prop() numberOfDigits: number = config.cartConfig.numberOfDigits;

  /**
   * Step value for quantity spinner.
   * If not set, '1' is used as a default value.
   */
  @Prop() step = 1;

  /** Label for 'Quantity changed' warning message */
  @Prop() quantityChangedWarning = "Quantity changed";

  /** Title attribute for 'input quantity' */
  @Prop() inputQuantityTitle: string;

  /** Title attribute for 'increase quantity' */
  @Prop() increaseQuantityTitle: string;

  /** Title attribute for 'decrease quantity' */
  @Prop() decreaseQuantityTitle: string;

  @Prop() productCode: string;

  @Prop() disabled = false;

  @Watch("minimumQuantity")
  minimumQuantityHandler(): void {
    this.updateQuantity(this.quantity, false);
  }

  @Event() private quantityChanged: EventEmitter<QuantityUpdate>;

  @State() private warningMessage = "";

  private quantityInput: HTMLInputElement;

  componentWillLoad(): void {
    this.warningMessage = "";
    this.updateQuantity(this.initialValue, false);
  }

  /**
   * Resets input quantity to initial value
   */
  @Method()
  async reset(showWarningMessage = true): Promise<void> {
    this.updateQuantity(this.initialValue, showWarningMessage);
  }

  // This is required to prevent typing non number value
  private inputQuantity = (event: InputEvent): void => {
    this.quantityInput.value = (event.target as HTMLInputElement).value.replace(
      /[^0-9]/g,
      ""
    );
  };

  private changeQuantity(event): void {
    const value = +event.target.value;
    this.updateQuantity(value);
    this.quantityInput.value = this.quantity.toString();
  }

  private increaseQuantity(): void {
    const quantity = this.quantity + this.step;
    this.updateQuantity(quantity);
  }

  private decreaseQuantity(): void {
    const quantity = this.quantity - this.step;
    this.updateQuantity(quantity);
  }

  private updateQuantity(value: number, showWarning = true) {
    let quantity = value;
    this.warningMessage = "";

    quantity = this.validateQuantity(quantity);
    if (quantity != value && showWarning) {
      this.warningMessage = this.quantityChangedWarning;
    }
    this.quantity = quantity;
    this.quantityChanged.emit({
      productCode: this.productCode,
      quantity,
    });
  }

  private validateQuantity(quantity: number) {
    if (quantity < this.minimumQuantity) {
      quantity = this.minimumQuantity;
    }
    if (quantity > this.maximumQuantity) {
      quantity = this.maximumQuantity - (this.maximumQuantity % this.step);
    }
    if ((quantity - this.minimumQuantity) % this.step != 0) {
      quantity =
        quantity + this.step - ((quantity - this.minimumQuantity) % this.step);
    }
    return quantity;
  }

  render() {
    return (
      <div>
        <div class="quantity-selector">
          <input
            ref={(e: HTMLInputElement) => (this.quantityInput = e)}
            value={this.quantity}
            disabled={this.disabled}
            class="quantity-input-field no-spinners"
            onInput={this.inputQuantity}
            onChange={(ev) => this.changeQuantity(ev)}
            title={this.inputQuantityTitle}
            maxLength={this.numberOfDigits}
            aria-label="quantity-input-field"
            tabindex="0"
          />

          <div class="quantity-button-group">
            <button
              disabled={this.disabled || this.quantity <= this.minimumQuantity}
              class="quantity-button"
              onClick={() => this.decreaseQuantity()}
              title={this.decreaseQuantityTitle}
            >
              <svg class="A-Icon A-Icon--less-minimize" width="15" height="15">
                <title>Icon less-minimize</title>
                <use xlinkHref="#less-minimize" />
              </svg>
            </button>
            <button
              disabled={this.disabled || this.quantity >= this.maximumQuantity}
              class="quantity-button"
              onClick={() => this.increaseQuantity()}
              title={this.increaseQuantityTitle}
            >
              <svg class="A-Icon A-Icon--bcp-plus" width="15" height="15">
                <title>Icon plus</title>
                <use xlinkHref="#bcp-plus"></use>
              </svg>
            </button>
          </div>
        </div>
        <div class="cart-quantity-warning">
          <span>{this.warningMessage}</span>
        </div>
      </div>
    );
  }
}
